/* ==========================================================================
   OMG Watch Co. · omg2-motion.js  (v01 · 2026-10-12 · draft)
   Vanilla JS, no libraries. Progressive enhancement: every section works
   and reads fine without this file. Respects prefers-reduced-motion.
   Load once with `defer` in layout/theme.liquid (see INSTALL.md).
   ========================================================================== */
(function () {
  'use strict';

  var doc = document.documentElement;
  doc.classList.remove('no-omg2-js');
  doc.classList.add('omg2-js');

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var ticking = false;
  var scrollers = [];

  function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }

  /* progress of an element through the viewport: 0 when its top meets the
     viewport bottom, 1 when its top reaches `endAt` (fraction of viewport) */
  function enterProgress(el, endAt) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || 1;
    return clamp((vh - r.top) / (vh * (1 - endAt)), 0, 1);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      for (var i = 0; i < scrollers.length; i++) scrollers[i]();
    });
  }

  /* ---------- scroll progress bar + nav tighten ---------- */
  function initNav() {
    var nav = document.querySelector('[data-omg2-nav]');
    var bar = document.querySelector('[data-omg2-progress]');
    if (!nav && !bar) return;
    scrollers.push(function () {
      var y = window.scrollY || window.pageYOffset;
      if (nav) nav.classList.toggle('is-scrolled', y > 60);
      if (bar) {
        var max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        bar.style.setProperty('--omg2-scroll', (y / max).toFixed(4));
      }
    });
  }

  /* ---------- reveal on enter (fade-up, unveil, stagger) ---------- */
  function initReveal() {
    var els = document.querySelectorAll('[data-omg2-reveal], [data-omg2-unveil]');
    if (!els.length) return;
    if (reduce.matches || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- expanding image: rounded card grows to full width ---------- */
  function initExpand() {
    var els = document.querySelectorAll('[data-omg2-expand]');
    if (!els.length) return;
    if (reduce.matches) { els.forEach(function (el) { el.style.setProperty('--p', 1); }); return; }
    scrollers.push(function () {
      els.forEach(function (el) { el.style.setProperty('--p', enterProgress(el, 0.25).toFixed(4)); });
    });
  }

  /* ---------- gentle parallax on images ---------- */
  function initParallax() {
    var els = document.querySelectorAll('[data-omg2-parallax]');
    if (!els.length || reduce.matches) return;
    scrollers.push(function () {
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var r = el.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        var center = (r.top + r.height / 2 - vh / 2) / vh; // -1..1
        el.style.setProperty('--omg2-shift', (center * -36).toFixed(1) + 'px');
      });
    });
  }

  /* ---------- stacking cards recede as the next one covers them ---------- */
  function initStack() {
    var pins = document.querySelectorAll('[data-omg2-stack] .omg2-stackcard-pin');
    if (pins.length < 2 || reduce.matches) return;
    scrollers.push(function () {
      for (var i = 0; i < pins.length - 1; i++) {
        var next = pins[i + 1].getBoundingClientRect();
        var cur = pins[i].getBoundingClientRect();
        var overlap = clamp((cur.bottom - next.top) / Math.max(1, cur.height), 0, 1);
        pins[i].firstElementChild.style.setProperty('--omg2-recede', (1 - overlap * 0.08).toFixed(4));
      }
    });
  }

  /* ---------- count-up stats ---------- */
  function initCount() {
    var els = document.querySelectorAll('[data-omg2-count]');
    if (!els.length) return;
    function show(el, n) { el.textContent = Math.round(n).toLocaleString('en-US'); }
    if (reduce.matches || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { show(el, +el.getAttribute('data-omg2-count')); });
      return;
    }
    els.forEach(function (el) { show(el, 0); });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        var el = e.target, to = +el.getAttribute('data-omg2-count'), t0 = null, dur = 1600;
        function step(ts) {
          if (!t0) t0 = ts;
          var p = clamp((ts - t0) / dur, 0, 1);
          show(el, to * (1 - Math.pow(1 - p, 3)));
          if (p < 1) window.requestAnimationFrame(step);
        }
        window.requestAnimationFrame(step);
      });
    }, { threshold: 0.6 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- pinned UV-LED story: steps drive the visual ---------- */
  function initUv() {
    document.querySelectorAll('[data-omg2-uv]').forEach(function (root) {
      var visual = root.querySelector('[data-omg2-uv-visual]');
      var steps = root.querySelectorAll('[data-omg2-uv-step]');
      if (!visual || !steps.length) return;
      if (!('IntersectionObserver' in window)) { visual.setAttribute('data-step', '1'); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          steps.forEach(function (s) { s.classList.toggle('is-active', s === e.target); });
          visual.setAttribute('data-step', e.target.getAttribute('data-omg2-uv-step'));
        });
      }, { rootMargin: '-45% 0px -45% 0px' });
      steps.forEach(function (s) { io.observe(s); });
    });
  }

  /* ---------- chapter filter chips ---------- */
  function initFilter() {
    document.querySelectorAll('[data-omg2-filter]').forEach(function (root) {
      if (root.hasAttribute('data-omg2-bound')) return;
      root.setAttribute('data-omg2-bound', '');
      var chips = root.querySelectorAll('[data-omg2-chip]');
      var cards = root.querySelectorAll('[data-omg2-card]');
      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var key = chip.getAttribute('data-omg2-chip');
          chips.forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
          cards.forEach(function (card) {
            card.hidden = !(key === 'all' || card.getAttribute('data-omg2-card') === key);
          });
        });
      });
    });
  }

  /* ---------- toast ---------- */
  var toastEl, toastTimer;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'omg2-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      toastEl.innerHTML = '<span class="omg2-toast__tick" aria-hidden="true">&#10003;</span><span class="omg2-toast__msg"></span>';
      document.body.appendChild(toastEl);
    }
    toastEl.querySelector('.omg2-toast__msg').textContent = msg;
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-on'); }, 2600);
  }

  /* ---------- quick add (Shopify AJAX cart API) ---------- */
  function refreshCount() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-omg2-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
        document.dispatchEvent(new CustomEvent('omg:cart-updated', { detail: cart }));
        return cart;
      });
  }
  function initQuickAdd() {
    document.addEventListener('submit', function (ev) {
      var form = ev.target;
      if (!form.matches || !form.matches('[data-omg2-quick-add]')) return;
      if (!window.fetch) return; // fall back to normal /cart/add submit
      ev.preventDefault();
      var btn = form.querySelector('button');
      var name = form.getAttribute('data-omg2-name') || 'Watch';
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      if (btn) btn.setAttribute('aria-busy', 'true');
      var vid = +form.querySelector('[name="id"]').value;
      var drawer = document.querySelector('cart-drawer');
      var useDrawer = !!(drawer && typeof drawer.renderContents === 'function');
      var payload = { items: [{ id: vid, quantity: 1 }] };
      if (useDrawer) { payload.sections = 'cart-drawer,cart-icon-bubble'; payload.sections_url = window.location.pathname; }
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) { if (!r.ok) throw r; return r.json(); })
        .then(function (data) {
          if (useDrawer && data && data.sections) {
            drawer.classList.remove('is-empty');
            drawer.renderContents({ id: vid, sections: data.sections });
            return;
          }
          return refreshCount().then(function () { toast(form.getAttribute('data-omg2-added') || (name + ' added to your bag')); });
        })
        .catch(function () { form.submit(); })
        .then(function () { if (btn) btn.removeAttribute('aria-busy'); });
    });
  }


  /* ---------- mobile sticky add-to-cart (product page) ---------- */
  function initStickyAtc() {
    document.querySelectorAll('[data-omg2-scroll-to]').forEach(function (b) {
      if (b.hasAttribute('data-omg2-bound')) return;
      b.setAttribute('data-omg2-bound', '');
      b.addEventListener('click', function () {
        var sel = b.getAttribute('data-omg2-scroll-to').split(',');
        var t = null;
        for (var i = 0; i < sel.length && !t; i++) { var c = document.querySelector(sel[i].trim()); if (c && c.offsetParent !== null && !c.hasAttribute('data-omg2-sticky-atc')) t = c; }
        if (!t) t = document.querySelector('.product-form__submit');
        if (!t) return;
        t.scrollIntoView({ block: 'center', behavior: reduce.matches ? 'auto' : 'smooth' });
        setTimeout(function () { t.focus({ preventScroll: true }); }, 450);
      });
    });
    document.querySelectorAll('[data-omg2-sticky-atc]').forEach(function (bar) {
      if (bar.hasAttribute('data-omg2-bound')) return;
      bar.setAttribute('data-omg2-bound', '');
      var target = document.querySelector(bar.getAttribute('data-omg2-watch'));
      if (!target || !('IntersectionObserver' in window)) return;
      var go = bar.querySelector('[data-omg2-sticky-go]');
      if (go) go.addEventListener('click', function () { target.scrollIntoView({ block: 'center', behavior: reduce.matches ? 'auto' : 'smooth' }); setTimeout(function () { target.focus({ preventScroll: true }); }, 400); });
      var io = new IntersectionObserver(function (entries) {
        var e = entries[0];
        var below = e.boundingClientRect.top < 0; // button scrolled above the viewport
        var on = !e.isIntersecting && (below || e.boundingClientRect.top > window.innerHeight);
        bar.classList.toggle('is-on', on);
        bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      }, { threshold: 0 });
      io.observe(target);
    });
  }

  /* ---------- gift finder ---------- */
  function initFinder() {
    document.querySelectorAll('[data-omg2-finder]').forEach(function (root) {
      if (root.hasAttribute('data-omg2-bound')) return;
      root.setAttribute('data-omg2-bound', '');
      var toggle = root.querySelector('[data-omg2-finder-toggle]');
      var panel = root.querySelector('[data-omg2-finder-panel]');
      var options = root.querySelector('[data-omg2-finder-options]');
      var answer = root.querySelector('[data-omg2-finder-answer]');
      var text = root.querySelector('[data-omg2-finder-text]');
      if (!toggle || !panel) return;
      function open(state) {
        panel.hidden = !state;
        toggle.setAttribute('aria-expanded', state ? 'true' : 'false');
        if (state) { var first = panel.querySelector('button'); if (first) first.focus(); }
        else toggle.focus();
      }
      toggle.addEventListener('click', function () { open(panel.hidden); });
      root.querySelectorAll('[data-omg2-finder-close]').forEach(function (b) { b.addEventListener('click', function () { open(false); }); });
      root.querySelectorAll('[data-omg2-finder-opt]').forEach(function (b) {
        b.addEventListener('click', function () {
          text.textContent = b.getAttribute('data-answer');
          options.hidden = true;
          answer.hidden = false;
          document.dispatchEvent(new CustomEvent('omg:gift-finder', { detail: { audience: b.getAttribute('data-omg2-finder-opt') } }));
        });
      });
      root.querySelectorAll('[data-omg2-finder-reset]').forEach(function (b) {
        b.addEventListener('click', function () { options.hidden = false; answer.hidden = true; });
      });
      root.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !panel.hidden) open(false); });
    });
  }


  /* ---------- curtain footer + back to top ---------- */
  function initFooter() {
    document.querySelectorAll('[data-omg2-top]').forEach(function (b) {
      if (b.hasAttribute('data-omg2-bound')) return;
      b.setAttribute('data-omg2-bound', '');
      b.addEventListener('click', function () { window.scrollTo({ top: 0, behavior: reduce.matches ? 'auto' : 'smooth' }); });
    });
  }

  function init() {
    initNav(); initReveal(); initExpand(); initParallax(); initStack();
    initCount(); initUv(); initFilter(); initQuickAdd(); initFinder(); initFooter(); initStickyAtc();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* re-run after the theme editor adds or reorders a section */
  document.addEventListener('shopify:section:load', function () { initReveal(); initCount(); initUv(); initFilter(); initFinder(); initFooter(); onScroll(); });
})();
